import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { db, Document, unmarshallFile } from "../db";
import { setDiskSpaceLimited } from "./DocListSynchroniser";
import { getAvailableStorageSpace } from "./Storage";
import { UpdateSpec } from "dexie";
import { closeSnackbar, enqueueSnackbar } from "notistack";

let timer: any;
let running = false;
let openFileOnDownload: string | undefined = undefined;
let openTarget: string = "_self";

export class FileDownload {
  abortController: AbortController;
  openOnDownload: boolean;
  percentCompleted: number | undefined = 0;
  doc: Document;
  target: string;

  constructor(doc: Document, openOnDownload: boolean, target: string = "_self") {
    this.doc = doc;
    this.target = target;
    this.openOnDownload = openOnDownload;
    this.abortController = new AbortController();
  }

  async download() {
    const res = await fetch(`/${encodeURI(this.doc.key)}`, {
      mode: "cors",
      cache: "no-cache",
      signal: this.abortController.signal,
    });

    if (!res.ok) {
      throw new DownloadFailedError(`Error fetching data: ${res.status}: ${res.statusText}- ${await res.text()}`, res.status);
    }

    const lastMod = res.headers.has("last-modified") ? new Date(res.headers.get("last-modified")!) : new Date();
    const contentLengthStr = res.headers.get("x-amzn-remapped-content-length");
    const contentLength = contentLengthStr ? parseInt(contentLengthStr) : 0;
    const reader = res.body?.getReader();
    if (!reader) {
      throw new Error("No reader");
    }

    let bytesRead = 0;
    let progress: ReadableStreamReadResult<Uint8Array>;
    let parts: Uint8Array[] = [];
    do {
      progress = await reader.read();
      if (progress.value) {
        parts.push(progress.value);
        bytesRead += progress.value.length;
        this.percentCompleted = contentLength ? Math.round((100 * bytesRead) / contentLength) : undefined;
        if (updateDownloadStatus) {
          updateDownloadStatus();
        }
      }
    } while (!progress.done);
    const fileBytes = (await unmarshallFile(new Blob(parts)))!;
    const etag = res.headers.get("etag") ?? undefined;

    if (!this.abortController.signal.aborted) {
      console.log("Storing file", this.doc.key, Object.fromEntries(res.headers.entries()), etag, lastMod);
      await db.transaction("rw", ["files", "documents"], async (tx) => {
        await tx.files.put(fileBytes, this.doc.key);
        const docUpdate: Partial<Document> = {
          download_order: undefined,
          stored_etag: etag,
          last_modified: lastMod.getTime(),
          downloaded_at: Date.now(),
        };

        await tx.documents.update(this.doc.id, docUpdate);
      });

      if (this.openOnDownload) {
        console.log("Opening file just downloaded", this.doc.key);
        openFileOnDownload = undefined;
        openTarget = "_self";
        launchDownloadedFile(this.doc, this.target);
      }
    }
  }

  abort() {
    this.abortController.abort();
  }
}

async function getHeadOfDownloadQueue() {
  return await db.documents.where("download_order").above(0).first();
}

class DownloadFailedError extends Error {
  statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;

    Object.setPrototypeOf(this, DownloadFailedError.prototype);
  }
}

async function scanForDownloads() {
  try {
    if (!navigator.onLine) {
      return;
    }

    let doc = await getHeadOfDownloadQueue();
    while (doc) {
      let availableSpace = await getAvailableStorageSpace();
      if (doc.size > availableSpace) {
        setDiskSpaceLimited(true);

        console.log("No space for download");
        // TODO set a flag;
        return;
      } else {
        setDiskSpaceLimited(false);
      }

      console.log("Downloading", doc.key);
      try {
        const dl = new FileDownload(doc, openFileOnDownload === doc.key, openTarget);
        setCurrentDownload(dl);
        await dl.download();
      } catch (ex: any) {
        if (ex instanceof DownloadFailedError) {
          // Treat this as a permanent error. We won't try to download again until they click on the item.
          enqueueSnackbar("Error fetching file: " + ex, { variant: "error" });
          db.documents.update(doc.id, { download_order: undefined });
        } else {
          console.log("Other error trying to download.  Will try again", ex);
          // Re-throw the exception so that it will break out of the fetch loop and wait to try again.
          throw ex;
        }
      } finally {
        setCurrentDownload(undefined);
      }
      doc = await getHeadOfDownloadQueue();
    }

    // Wait for 5 seconds, then check again.
  } catch (ex) {
    console.log("Error downloading", ex);
  } finally {
    running = false;
    timer = setTimeout(scanForDownloads, 5000);
  }
}

export function startDownloader() {
  // Every 5 seconds, we check our downloads
  stopDownloader();
  if (!running) {
    running = true;
    scanForDownloads();
  }
}

export async function clearDownloadQueue() {
  if (currentDownload) {
    currentDownload.abort();
  }
  await db.transaction("rw", ["documents"], async (tx) => {
    const downloadqueue = await db.documents.where("download_order").aboveOrEqual(0).toArray();
    const updates: { key: number; changes: UpdateSpec<Document> }[] = downloadqueue.map((doc) => ({
      key: doc.id,
      changes: { download_order: undefined },
    }));
    await tx.documents.bulkUpdate(updates);
  });
}

export function openOnDownload(key: string, target: string = "_self") {
  openFileOnDownload = key;
  openTarget = target;
}

export async function launchDownloadedFile(doc: Document, target: string = "_self") {
  // If running in Airwatch, use our own PDF viewer.  otherwise, use the built in browser viewer.
  // We also use the embeded browser if running as a standalone app (or in minimal-ui) because locked down iOS shows minimal-ui without navigation buttons.
  try {
    console.log("Closing any open snacks");
    closeSnackbar();
    let url = `/${doc.key}`;
    const internalViewer =
      localStorage.getItem("dcms:internalPdf") === "true" || !window.matchMedia("(display-mode: browser)").matches;
    if (navigator.userAgent?.includes("AirWatch") || internalViewer) {
      url = `/pdfjs/web/viewer.html?file=${url}`;
    }
    window.open(url, target);
  } catch (ex) {
    window.alert("Error opening file " + ex);
  }
}

export function stopDownloader() {
  if (timer) {
    clearTimeout(timer);
  }
}

export async function addToDownloadQueue(docs: Document[]) {
  // TODO put a mutex or promise around this.

  const highest = await db.documents.where("download_order").above(-10000).reverse().first();
  let next_download_order = (highest?.download_order ?? 0) + 1;
  const updates: { key: number; changes: UpdateSpec<Document> }[] = docs.map((doc) => ({
    key: doc.id,
    changes: {
      download_order: next_download_order++,
    },
  }));

  await db.documents.bulkUpdate(updates);
  // Start the downloader (if it isn't already running)
  startDownloader();
}

export async function removeFromDownloadQueue(doc: Document) {
  if (currentDownload?.doc.key === doc.key) {
    currentDownload.abort();
  }
  await db.documents.update(doc.id, {
    download_order: undefined,
  });
}

interface FileDownloadState {
  key: string;
  percentCompleted: number | undefined;
}

export const FileDownloaderContext = createContext<FileDownloadState | undefined>(undefined);

let currentDownload: FileDownload | undefined = undefined;
let updateDownloadStatus: (() => void) | undefined = undefined;

function setCurrentDownload(newVal: FileDownload | undefined) {
  currentDownload = newVal;
  if (updateDownloadStatus) {
    updateDownloadStatus();
  }
}

export function useFileDownloadStatus() {
  return useContext(FileDownloaderContext);
}

export function FileDownloadStatusProvider(props: { children: ReactNode }) {
  const [currentState, setCurrentState] = useState<FileDownloadState | undefined>(undefined);

  useEffect(() => {
    updateDownloadStatus = () => {
      setCurrentState(
        currentDownload
          ? {
              key: currentDownload.doc.key,
              percentCompleted: currentDownload.percentCompleted,
            }
          : undefined
      );
    };
    return () => {
      updateDownloadStatus = undefined;
    };
  }, []);

  return <FileDownloaderContext.Provider value={currentState}>{props.children}</FileDownloaderContext.Provider>;
}
